
import { mapGetters, mapActions } from 'vuex';

// API
import * as apiPlayground from '../api/public/playground';
import * as apiGeonames from '../api/geonames/geo';

// Plugins
import * as apiLoc from '../libraries/geolocation';

// Mixins
import errorsMixin from '../mixins/error-handler';
import metaMixin from '../mixins/meta';

import { getPlaceNameWithPreposition } from '../libraries/utils';

export default {
  name: 'List',
  mixins: [errorsMixin, metaMixin],
  beforeRouteUpdate(to, from, next) {
    if (from.name === to.name) {
      if (to.params.userLocation) {
        this.wakeUp();
      } else {
        this.clearWakeUp();
      }
      const data = { ...to.params, ...to.query };
      this.$refs.resultListView.updateList(data);
    }
    next();
  },
  props: {
    lat: {
      type: Number,
      default: null,
    },
    lon: {
      type: Number,
      default: null,
    },
    place: {
      type: String,
      default: null,
    },
    userLocation: {
      type: Boolean,
      default: false,
    },
  },
  // N'est pas exécuté sur la page detail-list
  async asyncData({
    params, query, store, error,
  }) {
    if (process.server) {
      store.dispatch('menu/disableToolbar');
    }

    // Data
    let context = null;
    let content = null;
    let contextTitle = '';
    let contextGeo = false;
    let adminCities = null;
    const defaultErrors$ = [];
    const asyncErrors$ = [];

    // Liste API async
    const apiCalls = [];
    if (query.lat && query.lon) {
      const data = { lat: query.lat, lon: query.lon };
      apiCalls.push(
        apiGeonames.reverse(data, null).then((res) => {
          // Use lat and lon if searching by geographic coordinates
          res.result.location.lat = query.lat;
          res.result.location.lon = query.lon;
          context = res;
          context.geoSearch = true;
        }).catch((e) => {
          if (e?.response?.data) {
            asyncErrors$.push(e.response.data);
          }
          defaultErrors$.push({ env: 'geo', field: 'position' });
        }),
      );
      apiCalls.push(
        apiPlayground.list(data, null).then((res) => { content = res; })
          .catch((e) => {
            if (e?.response?.data) {
              asyncErrors$.push(e.response.data);
            }
            defaultErrors$.push({ env: 'playground_search', field: 'form' });
          }),
      );
    } else if (params.place) {
      apiCalls.push(
        apiGeonames.detail({ search: params.place }, null).then((res) => { context = res; })
          .catch((e) => {
            if (e?.response?.data) {
              asyncErrors$.push(e.response.data);
            }
            defaultErrors$.push({ env: 'geo', field: 'form' });
          }),
      );
      apiCalls.push(
        apiPlayground.list({ place: params.place }, null).then((res) => { content = res; })
          .catch((e) => {
            if (e?.response?.data) {
              asyncErrors$.push(e.response.data);
            }
            defaultErrors$.push({ env: 'playground_search', field: 'form' });
          }),
      );
    }

    // Attendre toute les API
    await Promise.allSettled(apiCalls);

    if (context) {
      contextTitle = context.result.city.name;
      contextGeo = context.geoSearch;
    } else if (process.server) {
      error({ statusCode: 404 });
    }

    if (content?.playgrounds.length <= 0) {
      const input = {
        size: 3,
        lat: query.lat,
        lon: query.lon,
        place: params.place,
      };
      await apiPlayground.adminCities(input, null).then((res) => {
        adminCities = res.results;
      }).catch((e) => {
        if (e?.response?.data) {
          asyncErrors$.push(e.response.data);
        }
        defaultErrors$.push({ env: 'search_city', field: 'form' });
      });
    }

    return {
      context, content, contextTitle, contextGeo, adminCities, asyncErrors$, defaultErrors$,
    };
  },
  data() {
    return {
      // // Fallback asyncdata
      context: null,
      content: null,
      adminCities: null,
      // DATA
      contextTitle: '',
      contextGeo: false,
      listPlaygrounds: [],
      listNearestCity: null,
      listReady: false,
      noResult: false,
      showNoResultDialog: false,
      noResultDialogOptions: {
        maxWidth: '33%',
        maxHeight: '90%',
        scrollable: true,
      },
      noResultDialogButtons: { closeText: 'Fermer' },
      pubSpace: 0, // get space around ads from CSS var to calculate layout accordingly
      scrollBoxReady: false, // AppBar must be init after ScrollBox to hide when scrollbox is scrolling
      // Observe AppBar to know if appBar is Hidden or not and display ScrollBox shadow accordingly
      appBarObserver: null,
      appBarHidden: false,
      isAppBarHideable: false,
      forceHiddingToolbar: false,
      // snackbar geoloc
      timeoutId: null,
      isComponentActive: false,
    };
  },
  head() {
    return {
      title: this.metaTitle,
      meta: this.meta([{
        hid: 'description',
        name: 'description',
        content: this.metaDescription,
      }]),
    };
  },
  computed: {
    ...mapGetters({
      getBackArrow: 'transferData/getBackArrow',
      getToolbarSize: 'screen/getToolbarSize',
      getToolbarEnabled: 'menu/getToolbarEnabled',
      getViewMode: 'preferences/getViewMode',
      getWindowWidth: 'screen/getWindowWidth',
      getCurrentSearch: 'transferData/getCurrentSearch',
      isHGte: 'screen/isHGte',
    }),

    listProps() {
      return {
        context: this.context ? {
          ...this.context,
          userLocation: this.userLocation,
        } : null,
        content: this.content,
        adminCities: this.adminCities,
        showList: this.displayList,
      };
    },

    displayList() {
      return this.getViewMode !== 'map' || this.getViewMode === 'list';
    },
    toolbarColor() {
      return this.$vuetify.theme.dark ? 'semi' : 'primary';
    },
    mobileLayout() {
      // Width must be enough for :
      // scrollbar + 728 AD + Side AD 160 + pub-space + padding list
      return !(this.getWindowWidth >= (15 + 728 + 160 + 5 * 2 + 8 * 2));
    },
    displayAppBar() {
      if ((!this.isAppBarHideable && this.appBarHidden) || this.forceHiddingToolbar) {
        return false;
      }
      return !this.getToolbarEnabled;
    },
    noResultCenter() {
      const lat = this.lat ?? this.getCurrentSearch?.location.lat;
      const lon = this.lon ?? this.getCurrentSearch?.location.lon;
      return { lat, lon };
    },
    localisationLabel() {
      if (this.contextTitle) {
        if (this.contextTitle.indexOf('Les ') === 0) {
          return `des ${this.contextTitle.substr(4).trim()}`;
        }
        if (this.contextTitle.indexOf('Le ') === 0) {
          return `du ${this.contextTitle.substr(3).trim()}`;
        }
        return `de ${this.contextTitle}`;
      }
      return '';
    },
    metaTitle() {
      if (this.contextTitle) {
        if (this.contextGeo) {
          return `Aires de jeux autour ${this.localisationLabel}`;
        }
        return `Aires de jeux ${getPlaceNameWithPreposition(this.contextTitle)}`;
      }
      return null;
    },
    metaDescription() {
      if (this.contextTitle) {
        return `Trouvez des aires de jeux gratuites proche ${this.localisationLabel}`;
      }
      return undefined;
    },
  },
  watch: {
    mobileLayout: {
      immediate: true,
      handler() {
        this.handleToolbar();
      },
    },
    displayAppBar() {
      this.disconnectAppBarObserver();
    },
    displayList() {
      this.showNoResultDialog = this.noResult && !this.displayList;
    },
    getCurrentSearch() {
      if (this.getCurrentSearch?.userLocation && this.isComponentActive) {
        this.wakeUp();
      }
    },
  },
  created() {
    if (process.client) {
      // Get space around pub for dynamic layout
      const pubSpaceCss = getComputedStyle(document.documentElement).getPropertyValue('--pub-space');
      this.pubSpace = parseInt(pubSpaceCss.replace('px', ''), 10);
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.getCurrentSearch?.userLocation) {
        this.wakeUp();
      }
    });
  },
  activated() {
    this.isComponentActive = true;
    this.$nextTick(() => {
      if (this.getCurrentSearch?.userLocation && !this.timeoutId) {
        this.wakeUp();
      }
    });
  },
  deactivated() {
    this.isComponentActive = false;
    this.clearWakeUp();
  },
  updated() {
    this.observeAppBarObserver();
  },
  unmounted() {
    this.disconnectAppBarObserver();
  },
  beforeDestroy() {
    this.clearWakeUp();
  },
  methods: {
    ...mapActions({
      updateDetailFromMap: 'preferences/updateDetailFromMap',
      enableToolbar: 'menu/enableToolbar',
      disableToolbar: 'menu/disableToolbar',
      initSnackbar: 'snackbar/initSnackbar',
    }),
    initContext(data) {
      this.context = data;
      this.contextTitle = data.contextTitle;
      this.contextGeo = data.contextGeo;
    },
    initContent(data) {
      this.content = data;
      this.listPlaygrounds = data.listPlaygrounds;
      this.noResult = data.noResult;
      this.listNearestCity = data.listNearestCity;
      this.showNoResultDialog = this.noResult && !this.displayList;
      this.listReady = true;
    },

    loadDetail(playground) {
      const listContext = {
        listPlace: this.place,
        listLat: this.lat,
        listLon: this.lon,
        userLocation: this.userLocation,
      };
      const admin = playground.admin ? playground.admin.alias : playground.country.alias;
      const place = playground.city.alias;
      this.$newRouter.push({
        name: 'detail-list',
        params: {
          admin, place, id: playground.id, ...listContext,
        },
      });

      this.updateDetailFromMap(this.getViewMode === 'map');
    },
    handleToolbar() {
      if (this.mobileLayout) {
        this.disableToolbar();
      } else {
        this.enableToolbar();
      }
    },
    observeAppBarObserver() {
      if (this.$refs.appBar && !this.appBarObserver) {
        this.appBarObserver = new MutationObserver((mutations) => {
          mutations.forEach((m) => {
            const newValue = m.target.getAttribute(m.attributeName);
            this.$nextTick(() => {
              this.appBarHidden = newValue.includes('v-app-bar--hide-shadow');
            });
          });
        });

        this.appBarObserver.observe(this.$refs.appBar.$el, {
          attributes: true,
          attributeOldValue: true,
          attributeFilter: ['class'],
        });
      }

      // if there is no appBar it can't be hidden
      this.appBarHidden = this.appBarHidden && !!this.$refs.appBar;
    },
    disconnectAppBarObserver() {
      if (this.appBarObserver) {
        this.appBarObserver.disconnect();
        this.appBarObserver = null;
      }
    },
    clearWakeUp() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },
    wakeUp() {
      this.clearWakeUp();
      this.timeoutId = setTimeout(() => {
        const snackbar = {
          options: {
            clearable: true,
            color: 'success',
            timeout: -1, // Use -1 to keep open indefinitely
          },
          button: {
            text: 'OK',
          },
          content: 'Mettre à jour ma position',
          actionCallback: () => this.updateGeolocation(),
        };
        this.initSnackbar(snackbar);
      }, 1000 * 60 * 15); // Toutes les 15 min
    },
    // Géolocalisation de l'utilisateur
    updateGeolocation() {
      this.errors$.reset('geo');
      if (this.displayList) {
        this.listReady = false;
        apiLoc.geolocation(true, this).then((data) => {
          const place = data.city.alias;
          const { lat, lon } = data.location;
          const admin = data.admin ? data.admin.alias : data.country.alias;
          const userLocation = data.userLocation;
          this.$newRouter.replace({
            name: 'list-city',
            params: {
              admin, place, userLocation,
            },
            query: { lat, lon },
          });
        }).catch(() => {
          const snackbar = {
            options: {
              color: 'error',
              timeout: -1, // Use -1 to keep open indefinitely
              clearable: true,
            },
            content: this.errors$.get('geo', 'position'),
          };
          this.initSnackbar(snackbar);
        }).finally(() => {
          this.listReady = true;
          this.wakeUp();
        });
      } else {
        this.$refs.resultMap.geoLocation(true);
        this.wakeUp();
      }
    },
  },
};
